<template>
  <div class="difficulty-container">
    <!-- Hero Header Section -->
    <v-card class="hero-header mb-6" elevation="0">
      <div class="hero-content">
        <div class="hero-icon-wrapper">
          <v-icon size="48" color="white">mdi-map-marker-alert</v-icon>
        </div>
        <div class="hero-text">
          <h1 class="hero-title">สภาพความยากลำบากในการปฏิบัติงาน</h1>
          <p class="hero-subtitle">ข้อมูลพื้นที่ปฏิบัติงานและสถานะความยากลำบากของสถานศึกษา</p>
        </div>
        <v-spacer></v-spacer>
        <v-chip
          class="position-chip"
          large
        >
          <v-icon left size="20">mdi-account-tie</v-icon>
          {{ user_status_type }}
        </v-chip>
      </div>
    </v-card>

    <!-- Status Overview Cards -->
    <v-row class="stats-row mb-6">
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-status" elevation="0">
          <div class="stat-icon" :class="isDifficultArea ? 'warning-bg' : 'success-bg'">
            <v-icon color="white" size="28">{{ difficultyIcon }}</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-label">สถานะพื้นที่</span>
            <span class="stat-value" :class="isDifficultArea ? 'warning-text' : 'success-text'">
              {{ isDifficultArea ? 'พื้นที่พิเศษ' : 'พื้นที่ปกติ' }}
            </span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-province" elevation="0">
          <div class="stat-icon province-bg">
            <v-icon color="white" size="28">mdi-map</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-label">ระดับจังหวัด</span>
            <span class="stat-value" :class="(users.province_difficulty === '1' || users.province_difficulty === 1) ? 'warning-text' : 'success-text'">
              {{ provinceStatusText }}
            </span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-prefecture" elevation="0">
          <div class="stat-icon prefecture-bg">
            <v-icon color="white" size="28">mdi-home-city</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-label">ระดับอำเภอ</span>
            <span class="stat-value" :class="(users.prefecture_difficulty === '1' || users.prefecture_difficulty === 1) ? 'warning-text' : 'success-text'">
              {{ prefectureStatusText }}
            </span>
          </div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="stat-card stat-card-date" elevation="0">
          <div class="stat-icon date-bg">
            <v-icon color="white" size="28">mdi-calendar-check</v-icon>
          </div>
          <div class="stat-content">
            <span class="stat-label">วันที่ตรวจสอบ</span>
            <span class="stat-value date-text">{{ formattedShortDate }}</span>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Main Content Grid -->
    <v-row>
      <!-- Left Column: School Info & Status -->
      <v-col cols="12" lg="7">
        <!-- School Information Card -->
        <v-card class="info-card mb-6" elevation="0">
          <div class="card-header school-header">
            <v-icon color="white" class="mr-3">mdi-school</v-icon>
            <span class="card-header-title">ข้อมูลสถานศึกษา</span>
          </div>
          <v-card-text class="card-body">
            <div class="school-name-section">
              <v-icon color="#dc2626" size="32" class="mr-3">mdi-domain</v-icon>
              <div class="school-name-content">
                <span class="school-label">ชื่อสถานศึกษา</span>
                <span class="school-name">{{ schoolName }}</span>
              </div>
            </div>

            <v-divider class="my-5"></v-divider>

            <v-row>
              <v-col cols="12" md="6">
                <div class="location-item">
                  <div class="location-icon-wrapper province-icon">
                    <v-icon color="white" size="20">mdi-map-marker</v-icon>
                  </div>
                  <div class="location-content">
                    <span class="location-label">จังหวัด</span>
                    <span class="location-value">{{ provinceName }}</span>
                    <v-chip
                      x-small
                      :color="(users.province_difficulty === '1' || users.province_difficulty === 1) ? '#fff4dc' : '#e8f5e9'"
                      :text-color="(users.province_difficulty === '1' || users.province_difficulty === 1) ? '#a86400' : '#2e7d32'"
                      class="mt-1"
                    >
                      {{ provinceStatusText }}
                    </v-chip>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="location-item">
                  <div class="location-icon-wrapper prefecture-icon">
                    <v-icon color="white" size="20">mdi-city</v-icon>
                  </div>
                  <div class="location-content">
                    <span class="location-label">อำเภอ</span>
                    <span class="location-value">{{ prefectureName }}</span>
                    <v-chip
                      x-small
                      :color="(users.prefecture_difficulty === '1' || users.prefecture_difficulty === 1) ? '#fff4dc' : '#e8f5e9'"
                      :text-color="(users.prefecture_difficulty === '1' || users.prefecture_difficulty === 1) ? '#a86400' : '#2e7d32'"
                      class="mt-1"
                    >
                      {{ prefectureStatusText }}
                    </v-chip>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Difficulty Status Card -->
        <v-card class="status-card" elevation="0">
          <div class="card-header" :class="isDifficultArea ? 'warning-header' : 'success-header'">
            <v-icon color="white" class="mr-3">{{ difficultyIcon }}</v-icon>
            <span class="card-header-title">สถานะพื้นที่ปฏิบัติงาน</span>
          </div>
          <v-card-text class="card-body">
            <!-- Main Status Alert -->
            <div class="status-alert" :class="isDifficultArea ? 'warning-alert' : 'success-alert'">
              <div class="alert-icon-wrapper">
                <v-icon :color="isDifficultArea ? '#a86400' : '#2e7d32'" size="48">
                  {{ difficultyIcon }}
                </v-icon>
              </div>
              <div class="alert-content">
                <h3 class="alert-title" :class="isDifficultArea ? 'warning-text' : 'success-text'">
                  {{ difficultyStatusText }}
                </h3>
                <p class="alert-description">{{ difficultyDescription }}</p>
              </div>
            </div>

            <!-- Status Details -->
            <v-row class="mt-5">
              <v-col cols="12" md="6">
                <div class="detail-card province-detail">
                  <div class="detail-header">
                    <v-icon color="#455a64" size="20" class="mr-2">mdi-map</v-icon>
                    <span>ระดับจังหวัด</span>
                  </div>
                  <div class="detail-content">
                    <v-chip
                      :color="(users.province_difficulty === '1' || users.province_difficulty === 1) ? '#a86400' : '#2e7d32'"
                      dark
                      small
                    >
                      <v-icon left small>
                        {{ (users.province_difficulty === '1' || users.province_difficulty === 1) ? 'mdi-alert' : 'mdi-check' }}
                      </v-icon>
                      {{ provinceStatusText }}
                    </v-chip>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="detail-card prefecture-detail">
                  <div class="detail-header">
                    <v-icon color="#d9777b" size="20" class="mr-2">mdi-home-city</v-icon>
                    <span>ระดับอำเภอ</span>
                  </div>
                  <div class="detail-content">
                    <v-chip
                      :color="(users.prefecture_difficulty === '1' || users.prefecture_difficulty === 1) ? '#a86400' : '#2e7d32'"
                      dark
                      small
                    >
                      <v-icon left small>
                        {{ (users.prefecture_difficulty === '1' || users.prefecture_difficulty === 1) ? 'mdi-alert' : 'mdi-check' }}
                      </v-icon>
                      {{ prefectureStatusText }}
                    </v-chip>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Right Column: Benefits & Warnings -->
      <v-col cols="12" lg="5">
        <!-- Benefits Card (if difficult area) -->
        <v-card class="benefit-card mb-6" elevation="0" v-if="isDifficultArea">
          <div class="card-header benefit-header">
            <v-icon color="white" class="mr-3">mdi-shield-star</v-icon>
            <span class="card-header-title">สิทธิประโยชน์พิเศษ</span>
          </div>
          <v-card-text class="card-body pa-0">
            <div class="benefit-list">
              <div class="benefit-item">
                <div class="benefit-icon">
                  <v-icon color="#2e7d32" size="20">mdi-cash-multiple</v-icon>
                </div>
                <div class="benefit-content">
                  <span class="benefit-title">เบี้ยยังชีพพิเศษ</span>
                  <span class="benefit-desc">สำหรับพื้นที่ชายแดนใต้</span>
                </div>
              </div>
              <div class="benefit-item">
                <div class="benefit-icon">
                  <v-icon color="#2e7d32" size="20">mdi-shield-check</v-icon>
                </div>
                <div class="benefit-content">
                  <span class="benefit-title">การคุ้มครองความปลอดภัย</span>
                  <span class="benefit-desc">มาตรการดูแลเพิ่มเติม</span>
                </div>
              </div>
              <div class="benefit-item">
                <div class="benefit-icon">
                  <v-icon color="#2e7d32" size="20">mdi-swap-horizontal</v-icon>
                </div>
                <div class="benefit-content">
                  <span class="benefit-title">โอกาสโยกย้ายพิเศษ</span>
                  <span class="benefit-desc">สิทธิพิเศษในการขอย้าย</span>
                </div>
              </div>
              <div class="benefit-item">
                <div class="benefit-icon">
                  <v-icon color="#2e7d32" size="20">mdi-medal</v-icon>
                </div>
                <div class="benefit-content">
                  <span class="benefit-title">เงินเพิ่มพิเศษ</span>
                  <span class="benefit-desc">ค่าตอบแทนพื้นที่พิเศษ</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Normal Area Card (if not difficult) -->
        <v-card class="normal-card mb-6" elevation="0" v-else>
          <div class="card-header normal-header">
            <v-icon color="white" class="mr-3">mdi-check-decagram</v-icon>
            <span class="card-header-title">พื้นที่ปกติ</span>
          </div>
          <v-card-text class="card-body text-center py-8">
            <div class="normal-icon-wrapper">
              <v-icon size="72" color="#2e7d32">mdi-shield-check</v-icon>
            </div>
            <h3 class="normal-title mt-4">สถานศึกษาตั้งอยู่ในพื้นที่ปกติ</h3>
            <p class="normal-desc mt-2">สามารถปฏิบัติงานได้ตามปกติ<br>ไม่มีข้อจำกัดด้านความปลอดภัย</p>
            <v-chip color="#e8f5e9" text-color="#2e7d32" class="mt-4">
              <v-icon left small>mdi-check-circle</v-icon>
              สิทธิประโยชน์ตามมาตรฐานทั่วไป
            </v-chip>
          </v-card-text>
        </v-card>

        <!-- Warning Card (if difficult area) -->
        <v-card class="warning-card mb-6" elevation="0" v-if="isDifficultArea">
          <div class="card-header caution-header">
            <v-icon color="white" class="mr-3">mdi-alert-circle</v-icon>
            <span class="card-header-title">ข้อควรระวัง</span>
          </div>
          <v-card-text class="card-body pa-0">
            <div class="warning-list">
              <div class="warning-item">
                <div class="warning-icon">
                  <v-icon color="#a86400" size="20">mdi-alert</v-icon>
                </div>
                <div class="warning-content">
                  <span class="warning-title">สถานการณ์ความไม่สงบ</span>
                  <span class="warning-desc">ในบางพื้นที่อาจมีเหตุการณ์</span>
                </div>
              </div>
              <div class="warning-item">
                <div class="warning-icon">
                  <v-icon color="#a86400" size="20">mdi-car-off</v-icon>
                </div>
                <div class="warning-content">
                  <span class="warning-title">ข้อจำกัดการเดินทาง</span>
                  <span class="warning-desc">ควรวางแผนการเดินทางล่วงหน้า</span>
                </div>
              </div>
              <div class="warning-item">
                <div class="warning-icon">
                  <v-icon color="#a86400" size="20">mdi-shield-alert</v-icon>
                </div>
                <div class="warning-content">
                  <span class="warning-title">มาตรการความปลอดภัย</span>
                  <span class="warning-desc">ต้องปฏิบัติตามอย่างเคร่งครัด</span>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- Additional Info Expansion -->
        <v-card class="expansion-card" elevation="0">
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header class="expansion-header">
                <div class="d-flex align-center">
                  <v-icon color="#dc2626" class="mr-3">mdi-information-outline</v-icon>
                  <span class="expansion-title">ข้อมูลเพิ่มเติม</span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <div class="info-table">
                  <div class="info-row">
                    <span class="info-key">รหัสจังหวัด</span>
                    <span class="info-val">{{ users.province_id || 'ไม่ระบุ' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-key">รหัสอำเภอ</span>
                    <span class="info-val">{{ users.prefecture_id || 'ไม่ระบุ' }}</span>
                  </div>
                  <div class="info-row">
                    <span class="info-key">สถานะจังหวัด</span>
                    <span class="info-val">
                      <v-chip x-small :color="provinceStatusColor" dark>
                        {{ users.province_difficulty === '1' ? 'ยาก' : 'ปกติ' }}
                      </v-chip>
                    </span>
                  </div>
                  <div class="info-row">
                    <span class="info-key">สถานะอำเภอ</span>
                    <span class="info-val">
                      <v-chip x-small :color="prefectureStatusColor" dark>
                        {{ users.prefecture_difficulty === '1' ? 'ยาก' : 'ปกติ' }}
                      </v-chip>
                    </span>
                  </div>
                  <div class="info-row">
                    <span class="info-key">วันที่ประเมิน</span>
                    <span class="info-val">{{ formattedCurrentDate }}</span>
                  </div>
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading Overlay -->
    <v-overlay :value="loading" opacity="0.8">
      <div class="text-center">
        <v-progress-circular
          indeterminate
          size="64"
          width="4"
          color="#dc2626"
        ></v-progress-circular>
        <p class="mt-4 white--text">กำลังโหลดข้อมูล...</p>
      </div>
    </v-overlay>
  </div>
</template>

<script>
export default {
  name: 'WorkDifficulty',

  data() {
    return {
      ApiKey: 'HRvec2021',
      users: {},
      loading: false
    }
  },

  async mounted() {
    await this.loadUserData()
  },

  methods: {
    async loadUserData() {
      try {
        this.loading = true
        const userSession = JSON.parse(localStorage.getItem('user')) || {}
        
        if (!userSession.id_card) {
          throw new Error('ไม่พบข้อมูลผู้ใช้ในระบบ')
        }

        const result = await this.$http.post('personnel_temporary.php', {
          ApiKey: this.ApiKey,
          id_card: userSession.id_card
        })

        this.users = result.data || {}
      } catch (error) {
        console.error('Error loading user data:', error)
      } finally {
        this.loading = false
      }
    }
  },

  computed: {
    user_status_type() {
      if (!this.users.user_status) return 'ไม่ระบุ'
      
      const statusMap = {
        teacher: 'ข้าราชการครู',
        director: 'ผู้อำนวยการวิทยาลัย',
        se_director: 'รองผู้อำนวยการวิทยาลัย',
        S: 'ศึกษานิเทศก์'
      }
      
      return statusMap[this.users.user_status] || 'ไม่ระบุ'
    },

    schoolName() {
      return this.users.agencys || 'ไม่ระบุชื่อสถานศึกษา'
    },

    provinceName() {
      return this.users.province_name || 'ไม่ระบุจังหวัด'
    },

    prefectureName() {
      return this.users.prefecture_name || 'ไม่ระบุอำเภอ'
    },

    isDifficultArea() {
      return (this.users.province_difficulty === '1' || this.users.province_difficulty === 1) || (this.users.prefecture_difficulty === '1' || this.users.prefecture_difficulty === 1)
    },

    provinceStatusColor() {
      return (this.users.province_difficulty === '1' || this.users.province_difficulty === 1) ? '#a86400' : '#2e7d32'
    },

    prefectureStatusColor() {
      return (this.users.prefecture_difficulty === '1' || this.users.prefecture_difficulty === 1) ? '#a86400' : '#2e7d32'
    },

    provinceStatusText() {
      return (this.users.province_difficulty === '1' || this.users.province_difficulty === 1) ? 'พื้นที่ยากลำบาก' : 'พื้นที่ปกติ'
    },

    prefectureStatusText() {
      return (this.users.prefecture_difficulty === '1' || this.users.prefecture_difficulty === 1) ? 'พื้นที่ยากลำบาก' : 'พื้นที่ปกติ'
    },

    difficultyIcon() {
      return this.isDifficultArea ? 'mdi-alert-circle' : 'mdi-check-circle'
    },

    difficultyStatusText() {
      if (this.isDifficultArea) {
        return 'พื้นที่พัฒนาพิเศษเฉพาะกิจจังหวัดชายแดนภาคใต้'
      }
      return 'พื้นที่ปกติ'
    },

    difficultyDescription() {
      if (this.isDifficultArea) {
        return 'สถานศึกษาตั้งอยู่ในเขตพัฒนาพิเศษเฉพาะกิจจังหวัดชายแดนภาคใต้ ตามประกาศกระทรวงการคลัง ได้รับสิทธิประโยชน์และการดูแลเป็นพิเศษ'
      }
      return 'สถานศึกษาตั้งอยู่ในพื้นที่ปกติ สามารถปฏิบัติงานได้ตามปกติโดยไม่มีข้อจำกัดพิเศษ'
    },

    formattedCurrentDate() {
      return new Date().toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },

    formattedShortDate() {
      return new Date().toLocaleDateString('th-TH', {
        day: 'numeric',
        month: 'short',
        year: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Prompt:wght@300;400;500;600;700&display=swap');

.difficulty-container {
  font-family: 'Prompt', sans-serif;
  padding: 24px;
  background: linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%);
  min-height: 100vh;
}

/* Hero Header */
.hero-header {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%);
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  box-shadow: 0 10px 40px rgba(220, 38, 38, 0.3);
}

.hero-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.06'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

.hero-content {
  display: flex;
  align-items: center;
  padding: 32px;
  position: relative;
  z-index: 1;
  flex-wrap: wrap;
  gap: 16px;
}

.hero-icon-wrapper {
  width: 80px;
  height: 80px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.hero-text {
  color: white;
}

.hero-title {
  font-size: 26px;
  font-weight: 600;
  margin: 0 0 6px 0;
}

.hero-subtitle {
  font-size: 14px;
  opacity: 0.9;
  margin: 0;
  font-weight: 300;
}

.position-chip {
  background: white !important;
  color: #dc2626 !important;
  font-weight: 500;
  border-radius: 12px;
}

/* Statistics Cards */
.stats-row {
  margin-top: -16px;
}

.stat-card {
  background: white;
  border-radius: 16px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  height: 100%;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.warning-bg { background: linear-gradient(135deg, #a86400 0%, #a86400 100%); }
.success-bg { background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%); }
.province-bg { background: linear-gradient(135deg, #455a64 0%, #455a64 100%); }
.prefecture-bg { background: linear-gradient(135deg, #d9777b 0%, #b3151b 100%); }
.date-bg { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); }

.stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stat-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 16px;
  font-weight: 600;
  color: #263238;
}

.warning-text { color: #a86400 !important; }
.success-text { color: #2e7d32 !important; }
.date-text { color: #dc2626 !important; }

/* Info Card */
.info-card, .status-card, .benefit-card, .normal-card, .warning-card, .expansion-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.04);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
}

.card-header {
  padding: 18px 24px;
  display: flex;
  align-items: center;
}

.card-header-title {
  font-size: 16px;
  font-weight: 600;
  color: white;
}

.school-header { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); }
.warning-header { background: linear-gradient(135deg, #a86400 0%, #a86400 100%); }
.success-header { background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%); }
.benefit-header { background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%); }
.normal-header { background: linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%); }
.caution-header { background: linear-gradient(135deg, #a86400 0%, #a86400 100%); }

.card-body {
  padding: 24px;
}

/* School Name Section */
.school-name-section {
  display: flex;
  align-items: flex-start;
  background: #fef2f2;
  padding: 20px;
  border-radius: 14px;
}

.school-name-content {
  display: flex;
  flex-direction: column;
}

.school-label {
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
}

.school-name {
  font-size: 18px;
  font-weight: 600;
  color: #dc2626;
  line-height: 1.4;
}

/* Location Items */
.location-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.location-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.province-icon { background: linear-gradient(135deg, #455a64 0%, #455a64 100%); }
.prefecture-icon { background: linear-gradient(135deg, #d9777b 0%, #b3151b 100%); }

.location-content {
  display: flex;
  flex-direction: column;
}

.location-label {
  font-size: 12px;
  color: #6b7280;
}

.location-value {
  font-size: 16px;
  font-weight: 500;
  color: #263238;
}

/* Status Alert */
.status-alert {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  border-radius: 16px;
}

.warning-alert {
  background: linear-gradient(135deg, #fff4dc 0%, #fff4dc 100%);
  border: 1px solid #e6b04d;
}

.success-alert {
  background: linear-gradient(135deg, #e8f5e9 0%, #e8f5e9 100%);
  border: 1px solid #81c784;
}

.alert-icon-wrapper {
  width: 80px;
  height: 80px;
  background: white;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.alert-content {
  flex: 1;
}

.alert-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
}

.alert-description {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
}

/* Detail Cards */
.detail-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
}

.detail-header {
  display: flex;
  align-items: center;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 10px;
}

.detail-content {
  display: flex;
  align-items: center;
}

/* Benefit & Warning Lists */
.benefit-list, .warning-list {
  padding: 0;
}

.benefit-item, .warning-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 18px 24px;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.2s ease;
}

.benefit-item:last-child, .warning-item:last-child {
  border-bottom: none;
}

.benefit-item:hover, .warning-item:hover {
  background: #fafafa;
}

.benefit-icon, .warning-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.benefit-icon {
  background: #e8f5e9;
}

.warning-icon {
  background: #fff4dc;
}

.benefit-content, .warning-content {
  display: flex;
  flex-direction: column;
}

.benefit-title, .warning-title {
  font-size: 14px;
  font-weight: 500;
  color: #263238;
}

.benefit-desc, .warning-desc {
  font-size: 12px;
  color: #6b7280;
  margin-top: 2px;
}

/* Normal Area Card */
.normal-icon-wrapper {
  width: 120px;
  height: 120px;
  background: #e8f5e9;
  border-radius: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.normal-title {
  font-size: 18px;
  font-weight: 600;
  color: #263238;
  margin: 0;
}

.normal-desc {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
  line-height: 1.6;
}

/* Expansion Panel */
.expansion-card {
  overflow: hidden;
}

.expansion-header {
  padding: 18px 24px !important;
  min-height: 64px !important;
  background: #fafafa;
}

.expansion-title {
  font-size: 15px;
  font-weight: 500;
  color: #263238;
}

.info-table {
  padding: 8px 0;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f3f4f6;
}

.info-row:last-child {
  border-bottom: none;
}

.info-key {
  font-size: 13px;
  color: #6b7280;
}

.info-val {
  font-size: 13px;
  font-weight: 500;
  color: #263238;
}

/* Animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stat-card {
  animation: fadeInUp 0.5s ease forwards;
  opacity: 0;
}

.stat-card:nth-child(1) { animation-delay: 0.1s; }
.stat-card:nth-child(2) { animation-delay: 0.2s; }
.stat-card:nth-child(3) { animation-delay: 0.3s; }
.stat-card:nth-child(4) { animation-delay: 0.4s; }

.info-card, .status-card, .benefit-card, .warning-card, .normal-card {
  animation: fadeInUp 0.5s ease forwards;
  animation-delay: 0.3s;
  opacity: 0;
}

/* Responsive */
@media (max-width: 960px) {
  .hero-content {
    padding: 24px;
  }

  .hero-icon-wrapper {
    width: 64px;
    height: 64px;
  }

  .hero-title {
    font-size: 20px;
  }

  .position-chip {
    width: 100%;
    margin-top: 8px;
    justify-content: center;
  }

  .status-alert {
    flex-direction: column;
    text-align: center;
  }

  .alert-icon-wrapper {
    margin: 0 auto;
  }
}

@media (max-width: 600px) {
  .difficulty-container {
    padding: 16px;
  }

  .stat-card {
    padding: 16px;
  }

  .stat-icon {
    width: 48px;
    height: 48px;
  }

  .school-name {
    font-size: 16px;
  }

  .card-body {
    padding: 16px;
  }
}
</style>